/**
 * TeamContext
 * Manages active team state. Fetches real team names from Keycloak via /api/auth/me,
 * falling back to JWT group names while loading.
 */

import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from './AuthContext'
import { customFetch } from '@/api/client'
import { useInternalTeams } from '@/hooks/useInternalTeams'
import { createChangeWatcher, groupsSignature } from '@/lib/membershipRefresh'
import {
  ACTIVE_TEAM_KEY,
  IMPERSONATE_TEAM_KEY,
  readTabScope,
  seedNewTabs,
  writeTabScope,
} from '@/lib/tabScope'
import { VANITY_ORG_SLUG } from '@/lib/vanity'

interface TeamInfo {
  id: string
  name: string
  slug: string
  /** Locked-down demo team (TRA-832): mutation UI hidden, API read-only. */
  is_restricted: boolean
  /** Sidebar routes hidden from this team (TRA-927). Empty = show all. */
  hidden_nav_items: string[]
  /** Route the team lands on after sign-in (TRA-927). "/" = Dashboard. */
  landing_page: string
  /** Whether the signed-in user owns this team. Gates the Billing page (TRA-1163). */
  is_owner: boolean
}

interface MeResponse {
  email: string
  active_team_id: string
  teams: TeamInfo[]
  is_employee: boolean
}

interface TeamContextValue {
  email: string | null
  teams: TeamInfo[]
  activeTeamId: string | null
  activeTeam: TeamInfo | null
  isEmployee: boolean
  /**
   * True once /api/auth/me has resolved. isEmployee is false until then,
   * so gates that redirect on it must wait for this instead of deciding
   * from the not-yet-loaded default.
   */
  meLoaded: boolean
  /**
   * True once /api/auth/me has either resolved or failed. On a failure the
   * canonical app carries on with JWT team data (no attributes), so a gate
   * that only wants to wait out the request, not fail closed, waits on this.
   */
  meSettled: boolean
  /**
   * True when the active team is a restricted demo team (TRA-832). Hides all
   * mutation UI — for employees too, so a live demo can't be accidentally
   * mutated. The API independently 403s non-employee mutations.
   */
  isRestricted: boolean
  /**
   * Sidebar routes hidden from the team whose data the app shows (TRA-927):
   * the impersonated team under staff impersonation, else the active one.
   * Defaults to [] until that config loads, which is NOT "show everything"
   * — pair it with navConfigLoaded via isNavRouteVisible, or a
   * default-hidden route leaks during the load window.
   */
  hiddenNavItems: string[]
  /**
   * True once hiddenNavItems and landingPage carry the real config: /me
   * has resolved, or, under impersonation, the staff team list has. Nav
   * gates wait on this rather than meLoaded so they follow the impersonated
   * team the way every data request does.
   */
  navConfigLoaded: boolean
  /**
   * True once navConfigLoaded is true or the request carrying the nav
   * config has failed: the nav analog of meSettled. A gate that wants to
   * wait out a pending config and then fall back, not fail closed, waits
   * on this.
   */
  navConfigSettled: boolean
  /**
   * That team's landing route (TRA-927), or null until its config has
   * loaded. Null (not "/") while loading so the landing redirect waits for
   * the real value instead of settling on the Dashboard and never firing.
   */
  landingPage: string | null
  /** Org slug this page is locked to via vanity subdomain, or null (TRA-829). */
  vanityOrg: string | null
  /** True when the signed-in user is not a member of the vanity org. */
  vanityDenied: boolean
  /**
   * Error message when /me failed on a vanity host. Vanity team resolution
   * needs /me's slugs (JWT fallback carries UUIDs), so unlike canonical
   * hosts a dead /me can't be papered over — surface it instead of
   * spinning on "Loading team..." forever.
   */
  vanityError: string | null
  switchTeam: (teamId: string) => void
  /** Team id this tab is impersonating (staff only), or null. */
  impersonatingTeamId: string | null
  /** Begin impersonating a team in this tab. Reloads to re-scope all data. */
  startImpersonation: (teamId: string) => void
  /** Stop impersonating in this tab and return to the staff member's own teams. */
  stopImpersonation: () => void
  refetch: () => void
}

const TeamContext = createContext<TeamContextValue | null>(null)

/** Leave impersonation in this tab: drop its target and reload to re-scope every cached query. */
function clearImpersonation(): void {
  writeTabScope(IMPERSONATE_TEAM_KEY, null)
  window.location.reload()
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Resolve the stored team selection against the current teams list.
 * The stored value may be a UUID or a group name (from JWT fallback).
 *
 * When meLoaded is false, teams come from JWT (names only). If the stored
 * value is a UUID, we trust it and return it as-is — the JWT fallback teams
 * can't validate UUIDs, so we wait for /me to load before trying to match.
 */
function resolveActiveTeamId(teams: TeamInfo[], meLoaded: boolean): string | null {
  const stored = readTabScope(ACTIVE_TEAM_KEY)

  if (stored) {
    // Direct match (stored value matches a team ID in the current list)
    if (teams.some(t => t.id === stored)) {
      return stored
    }

    // If /me hasn't loaded yet, teams are name-based from JWT.
    // A stored UUID won't match — trust it until /me arrives.
    if (!meLoaded && UUID_RE.test(stored)) {
      return stored
    }

    // Name match: stored value is a group name, teams now have real UUIDs
    // (handles the JWT-name → real-UUID transition after /me loads)
    const stripped = stored.replace(/^\//, '')
    const matchByName = teams.find(t => t.name === stripped || t.name === stored)
    if (matchByName) {
      // Migrate the stored name to the real UUID
      writeTabScope(ACTIVE_TEAM_KEY, matchByName.id)
      return matchByName.id
    }
  }

  // Default to first team
  const firstTeam = teams[0]
  if (firstTeam) {
    writeTabScope(ACTIVE_TEAM_KEY, firstTeam.id)
    return firstTeam.id
  }

  writeTabScope(ACTIVE_TEAM_KEY, null)
  return null
}

export function TeamProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { email, groups, isAuthenticated, teamCheckComplete } = useAuth()

  // Fetch real team names from Keycloak via /api/auth/me
  const meQuery = useQuery({
    queryKey: ['/api/auth/me'],
    queryFn: () => customFetch<MeResponse>('/api/auth/me'),
    enabled: isAuthenticated && teamCheckComplete,
    staleTime: 60_000, // Cache for 1 minute
  })

  const meLoaded = meQuery.data !== undefined
  const meSettled = meLoaded || meQuery.isError

  // The query key never changes when the token renews, so a token whose
  // groups claim gained or lost a team would leave the list stale until the
  // next page load (TRA-1576). Ask /me again whenever the claim changes;
  // refetch keeps the old list on screen while the new one loads. The
  // first render only records the claim, and until /me has answered once
  // its own first request carries the current token.
  const { refetch: refetchMe } = meQuery
  const signature = groupsSignature(groups.map((g) => g.id))
  const groupsChanged = useRef(createChangeWatcher())
  useEffect(() => {
    if (!groupsChanged.current(signature)) return
    if (!meSettled) return
    void refetchMe()
  }, [signature, meSettled, refetchMe])

  const isEmployee = meQuery.data?.is_employee ?? false

  // Read from this tab's scope, the same copy the header injector in
  // client.ts reads, so the banner and the requests always agree. Another
  // tab ending impersonation never changes it (lib/tabScope.ts).
  const impersonatingTeamId = readTabScope(IMPERSONATE_TEAM_KEY)

  // New tabs start in the scope of the tab that has focus.
  useEffect(() => {
    seedNewTabs()
    window.addEventListener('focus', seedNewTabs)
    return () => {
      window.removeEventListener('focus', seedNewTabs)
    }
  }, [])

  // Under staff impersonation every data request targets impersonatingTeamId
  // (client.ts sends X-Impersonate-Team), but /me never re-scopes: it lists
  // the staff member's own teams. The nav config has to follow the data, so
  // while impersonating it comes from the staff team list instead. Same
  // query as ImpersonationBanner and the Employees page, so one fetch.
  const impersonatedQuery = useInternalTeams({
    enabled: isEmployee && impersonatingTeamId !== null,
  })

  // Use real names from /me when available, fall back to JWT groups
  const teams: TeamInfo[] = meQuery.data
    ? meQuery.data.teams
    : groups.map(g => ({
        id: g.id,
        name: g.name,
        slug: g.slug,
        // JWT groups carry no attributes; until /me loads we can't know.
        // Unrestricted is the safe default only because the API enforces
        // restrictions server-side regardless of what the UI shows.
        is_restricted: false,
        hidden_nav_items: [],
        landing_page: '/',
        // Ownership needs /me; the API gates Billing on it regardless.
        is_owner: false,
      }))

  // switchTeam bumps this counter to force re-derivation (before page reloads)
  const [switchCounter, setSwitchCounter] = useState(0)

  // Derive activeTeamId from the tab scope + current teams list.
  // Re-derives when teams change (e.g. /me loads) or on explicit switch.
  //
  // On a vanity subdomain (TRA-829) the hostname wins over the stored team:
  // the page is locked to the org whose slug matches the subdomain. We
  // still write the resolved id to the tab scope because client.ts reads
  // it to inject X-Team-Id. Storage is per-origin, so this never leaks
  // into platform.trainloop.ai's own selection. Slug matching needs
  // /me (JWT-fallback teams carry UUIDs as slugs), so resolution stays
  // null until meLoaded; TeamGuard shows the loading state meanwhile.
  const activeTeamId = useMemo(() => {
    if (VANITY_ORG_SLUG !== null) {
      const match = teams.find(t => t.slug === VANITY_ORG_SLUG)
      if (match) {
        writeTabScope(ACTIVE_TEAM_KEY, match.id)
        return match.id
      }
      return null
    }
    return resolveActiveTeamId(teams, meLoaded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, meLoaded, switchCounter])

  // Non-member on a vanity subdomain: only decidable once /me has loaded.
  const vanityDenied = VANITY_ORG_SLUG !== null && meLoaded && activeTeamId === null

  const vanityError =
    VANITY_ORG_SLUG !== null && meQuery.isError ? meQuery.error.message : null

  const switchTeam = (teamId: string): void => {
    writeTabScope(ACTIVE_TEAM_KEY, teamId)
    setSwitchCounter(c => c + 1)
    window.location.reload() // Clear all cached data
  }

  const startImpersonation = (teamId: string): void => {
    writeTabScope(IMPERSONATE_TEAM_KEY, teamId)
    window.location.reload() // Re-scope every cached query to the target team
  }

  const refetch = (): void => {
    void meQuery.refetch()
  }

  const activeTeam = teams.find(t => t.id === activeTeamId) ?? null

  const isRestricted = activeTeam !== null && activeTeam.is_restricted

  // The team whose nav config applies: the impersonated one while
  // impersonating, since its data is what every page shows, else the
  // active one. Loaded/failed track whichever request carries it.
  const impersonating = impersonatingTeamId !== null
  const navTeam = impersonating
    ? (impersonatedQuery.data?.teams.find(t => t.id === impersonatingTeamId) ?? null)
    : activeTeam
  // Loaded means the nav team's config is in hand. A stored target the
  // loaded list no longer carries (the team was deleted mid-session) never
  // counts as loaded: an empty config would open the default-hidden
  // routes, so the effect below leaves impersonation instead.
  const navConfigLoaded = impersonating ? navTeam !== null : meLoaded
  // The staff list only runs once /me has proved staff status, so under
  // impersonation a failed /me fails the nav config too: otherwise it
  // would stay pending for good instead of settling on the fallback.
  const navConfigFailed = meQuery.isError || impersonatedQuery.isError
  const navConfigSettled = navConfigLoaded || navConfigFailed
  const targetMissing =
    impersonating && impersonatedQuery.data !== undefined && navTeam === null
  useEffect(() => {
    if (!targetMissing) return
    console.error(
      `Impersonated team ${impersonatingTeamId} is no longer listed; leaving impersonation.`
    )
    clearImpersonation()
  }, [targetMissing, impersonatingTeamId])

  const hiddenNavItems = navTeam?.hidden_nav_items ?? []
  // Landing is null until the nav team's real config has resolved — the JWT
  // fallback can't carry it, and redirecting on a placeholder "/" would
  // silently skip a team's configured landing page. A FAILED request is
  // different from a pending one: on canonical hosts the app stays usable
  // via the JWT fallback, so the landing settles on the Dashboard instead
  // of leaving "/" blank forever.
  const landingPage =
    navConfigLoaded && navTeam !== null
      ? navTeam.landing_page
      : navConfigFailed
        ? '/'
        : null

  return (
    <TeamContext.Provider
      value={{
        email,
        teams,
        activeTeamId,
        activeTeam,
        isEmployee,
        meLoaded,
        meSettled,
        isRestricted,
        hiddenNavItems,
        navConfigLoaded,
        navConfigSettled,
        landingPage,
        vanityOrg: VANITY_ORG_SLUG,
        vanityDenied,
        vanityError,
        switchTeam,
        impersonatingTeamId,
        startImpersonation,
        stopImpersonation: clearImpersonation,
        refetch,
      }}
    >
      {children}
    </TeamContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTeam = (): TeamContextValue => {
  const ctx = useContext(TeamContext)
  if (!ctx) {
    throw new Error('useTeam must be used within TeamProvider')
  }
  return ctx
}
